import React, { useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import Image from 'components/Image'
import { ProductDetail } from 'lib/hooks/use-products'
import { maxWidth, mobileMenuMaxWidth } from 'constants/styles'
import classnames from 'classnames'
import { useBrowser } from 'lib/hooks/use-browser'
import { wrap } from 'popmotion'
import { SliderBack } from 'components/svg/SliderBack'
import { SliderForward } from 'components/svg/SliderForward'

const variants = {
	enter: ( direction: number ) => {
		return {
			x: direction === 1 ? '130%' : direction === -1 ? '-130%' : 0,
			opacity: 0,
		}
	},
	center: {
		zIndex: 1,
		x: 0,
		opacity: 1,
	},
	exit: ( direction: number ) => {
		return {
			zIndex: 0,
			x: direction === 1 ? '-130%' : direction === -1 ? '130%' : 0,
			opacity: 0,
		}
	},
}

const swipeConfidenceThreshold = 10000
const swipePower = ( offset: number, velocity: number ) => {
	return Math.abs( offset ) * velocity
}

type Props = {
	product: Pick<ProductDetail, 'title' | 'images'>
	variant: ProductDetail['variants'][0]
}

export default function Slider({ product, variant }: Props ) {
	const images = [ variant.image, ...product.images ]

	const [ [ page, direction ], setPage ] = useState( [ 0, 0 ] )
	const paginate = ( direction: number ) => {
		setPage( [ page + direction, direction ] )
	}
	const index = wrap( 0, images.length, page )
	const current = images[ index ]

	const { isSafari } = useBrowser()

	return (
		<>
			<picture>
				<div>
					<AnimatePresence initial={false} custom={direction}>
						{current && (
							<Image
								key={page}
								src={current.originalSrc}
								custom={direction}
								className={classnames({
									variant: current.originalSrc === variant.image.originalSrc,
								})}
								alt={current.altText || product.title}
								sizes={
									`( max-width: ${mobileMenuMaxWidth}px ) 95vw, ` +
									`( max-width: ${maxWidth}px ) 63vw, ` +
									`${Math.round( ( maxWidth / 11 ) * 5 * 1.2 )}px`
								}
								variants={variants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{
									x: { type: 'spring', stiffness: 300, damping: 55 },
									opacity: { duration: 0.3 },
								}}
								drag="x"
								dragConstraints={{ left: 0, right: 0 }}
								dragElastic={1}
								onDragEnd={( e, { offset, velocity }) => {
									const swipe = swipePower( offset.x, velocity.x )

									if ( swipe < -swipeConfidenceThreshold ) {
										paginate( 1 )
									} else if ( swipe > swipeConfidenceThreshold ) {
										paginate( -1 )
									}
								}}
							/>
						)}
					</AnimatePresence>
				</div>
				{!!product.images.length && (
					<>
						<a className="slider-button back" onClick={() => paginate( -1 )}>
							<SliderBack />
						</a>
						<a className="slider-button forward" onClick={() => paginate( 1 )}>
							<SliderForward />
						</a>
					</>
				)}
			</picture>
			{!!product.images.length && (
				<legend>
					<ol>
						{images.map( ( image, i ) => (
							<li
								key={i}
								className={classnames({
									active: current.originalSrc === image.originalSrc,
								})}
								onClick={() => setPage( [ i, 0 ] )}
							>
								0{i + 1}
							</li>
						) )}
					</ol>
				</legend>
			)}
			<style jsx>{`
				picture {
					position: relative;
					height: 100%;
					max-width: none;
					min-height: 30vw;
					background-color: #f7f7f7;
				}
				picture div {
					position: relative;
					width: 100%;
					height: 100%;
				}
				picture :global(img) {
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					bottom: 0;
					width: 100%;
					max-width: none;
					object-fit: cover;
				}
				picture :global(img.variant) {
					top: 15%;
					left: 15%;
					width: 70%;
					height: 70%;
					object-fit: contain;
					object-position: bottom;
					${( !isSafari && 'image-rendering: -webkit-optimize-contrast;' )}
				}
				@media ( max-width: 720px ) {
					picture :global(img.variant) {
						top: 10%;
						left: 5%;
						width: 90%;
						height: 80%;
					}
				}
				picture .slider-button {
					position: absolute;
					top: 50%;
					width: 48px;
					height: 48px;
					transform: translateY(-50%);
					z-index: 2;
				}
				picture .slider-button.back {
					left: 36px;
				}
				picture .slider-button.forward {
					right: 36px;
				}
				@media ( max-width: 560px ) {
					picture .slider-button {
						width: 36px;
						height: 36px;
					}
					picture .slider-button.back {
						left: 20px;
					}
					picture .slider-button.forward {
						right: 20px;
					}
				}
				legend {
					grid-column: 1;
					grid-row: 11;
					margin-bottom: -40px;
				}
				legend ol {
					display: flex;
					gap: 1em;
					margin-top: 1em;
					margin-left: -2px;
				}
				legend li {
					font-size: 15px;
					color: #8a8f95;
					cursor: pointer;
					transition: color 0.3s;
				}
				legend li.active,
				legend li:hover {
					color: black;
				}
			`}</style>
		</>
	)
}
